<template>
    <div>
        <v-row>
            <v-col cols="12" md="8" lg="12">
                <v-card flat>
                    <v-card-text>
                        <!-- Safe Search -->
                        <v-switch v-model="currentDeviceSettings.safesearch" :label="$gettext('Safe Search')"
                            color="primary" class="mb-2" :hint="$gettext('Enable safe search on search engines')"
                            persistent-hint></v-switch>

                        <!-- YouTube Restricted Mode -->
                        <v-switch v-model="currentDeviceSettings.youtube_restricted"
                            :label="$gettext('YouTube Restricted Mode')" color="primary" class="mb-2"
                            :hint="$gettext('Enable restricted mode on YouTube')" persistent-hint></v-switch>

                        <!-- NextDNS List -->
                        <v-switch v-model="currentDeviceSettings.nextdns_list" :label="$gettext('NextDNS List')"
                            color="primary" class="mb-2" :hint="$gettext('Enable NextDNS filtering')"
                            persistent-hint></v-switch>

                        <!-- OISD -->
                        <v-switch v-model="currentDeviceSettings.oisd" :label="$gettext('OISD')" color="primary"
                            class="mb-2"
                            :hint="$gettext('OISD is a general purpose blocklist, that compiles many other blocklists into one, and tries to avoid false positives.')"
                            persistent-hint>
                            <template v-slot:label>
                                <div>
                                    OISD
                                    <v-btn text small color="primary" href="https://oisd.nl" target="_blank"
                                        class="ml-2">
                                    </v-btn>
                                </div>
                            </template>
                        </v-switch>

                        <!-- AdGuard Mobile Ads -->
                        <!-- <v-switch
                            v-model="currentDeviceSettings.adguard_mobile"
                            :label="$gettext('AdGuard Mobile Ads')"
                            color="primary"
                            class="mb-2"
                        >
                            <template v-slot:label>
                                <div>
                                    {{ $gettext('AdGuard Mobile Ads') }}
                                    <v-btn
                                        text
                                        small
                                        color="primary"
                                        href="https://kb.adguard.com/general/adguard-ad-filters#mobile-ads-filter"
                                        target="_blank"
                                        class="ml-2"
                                    >
                                        <v-icon small>{{ icons.mdiOpenInNew }}</v-icon>
                                        {{ $gettext('Learn More') }}
                                    </v-btn>
                                </div>
                            </template>
                        </v-switch> -->

                        <!-- Secure Google Navigation -->
                        <!-- <v-switch
                            v-model="currentDeviceSettings.secure_google"
                            :label="$gettext('Secure Navigation on Google')"
                            color="primary"
                            class="mb-2"
                            :hint="$gettext('Enable secure navigation on Google')"
                            persistent-hint
                        ></v-switch> -->

                        <!-- Block Trackers -->
                        <!-- <v-switch
                            v-model="currentDeviceSettings.block_trackers"
                            :label="$gettext('Block Trackers')"
                            color="primary"
                            class="mb-2"
                            :hint="$gettext('Block tracking and analytics')"
                            persistent-hint
                        ></v-switch> -->

                        
                        <v-switch
                            v-model="hotspotSharingEnabled"
                            :label="$gettext('Enable Hotspot Sharing')"
                            color="primary"
                            class="mt-0"
                            :hint="$gettext('Allow the user to share their internet connection')"
                            persistent-hint
                        ></v-switch>

                        <div v-if="hotspotSharingEnabled" class="ml-4 mt-2">
                             <v-radio-group
                                v-model="hotspotSharingMode"
                                row
                                class="mt-0"
                            >
                                <v-radio
                                    :label="$gettext('Fixed Duration')"
                                    value="fixed"
                                ></v-radio>
                                <v-radio
                                    :label="$gettext('Indefinitely')"
                                    value="indefinite"
                                ></v-radio>
                            </v-radio-group>

                            <v-fade-transition>
                                <v-text-field
                                    v-if="hotspotSharingMode === 'fixed'"
                                    v-model.number="hotspotSharingDuration"
                                    :label="$gettext('Duration (minutes)')"
                                    type="number"
                                    outlined
                                    dense
                                    class="max-width-200 mt-2"
                                    :rules="hotspotDurationRules"
                                    min="1"
                                ></v-text-field>
                            </v-fade-transition>

                             <div v-if="currentDeviceHotspotSharing && currentDeviceHotspotSharing.createdAt" class="caption grey--text mt-2">
                                {{ $gettext('Started at:') }} {{ formatDateTime(currentDeviceHotspotSharing.createdAt) }}
                            </div>
                        </div>

                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import { mapWritableState, mapState } from 'pinia'
import { useSecurityStore } from '@/stores/SecurityStore'
import { usePhonesStore } from '@/stores/PhonesStore'
import { useAppUsageRestrictionsStore } from '@/stores/AppUsageRestrictionsStore'
import MixinIcon from '@/mixins/MixinIcon'

export default {
    name: 'SecurityTab',
    mixins: [MixinIcon],
    computed: {
        ...mapWritableState(useSecurityStore, {
            securitySettings: 'securitySettings',
            currentDeviceId: 'currentDeviceId'
        }),
        ...mapWritableState(usePhonesStore, {
            currentSelectedConfiguration: 'currentSelectedConfiguration',
            configurations: 'configurations'
        }),
        ...mapState(useAppUsageRestrictionsStore, {
            restrictions: 'restrictions'
        }),
        currentDeviceSettings() {
            const deviceConfig = this.configurations.find(
                config => config.key === this.currentSelectedConfiguration
            );
            if (deviceConfig?.value?.userInfo?.mdmDeviceId) {
                this.currentDeviceId = deviceConfig.value.userInfo.mdmDeviceId;
                return this.securitySettings[this.currentDeviceId] || {
                    safesearch: true,
                    youtube_restricted: true,
                    nextdns_list: true,
                    oisd: false,
                    adguard_mobile: false,
                    secure_google: true,
                    block_trackers: false
                };
            }
            return null;
        },
        currentDeviceHotspotSharing() {
             if (!this.currentDeviceId || !this.restrictions[this.currentDeviceId]) return null;
             return this.restrictions[this.currentDeviceId]?.hotspotSharing || null;
        },
        hotspotSharingEnabled: {
            get() {
                return this.currentDeviceHotspotSharing?.enabled || false;
            },
            set(value) {
                const store = useAppUsageRestrictionsStore();
                if (value) {
                    store.setHotspotSharingForDevice(this.currentDeviceId, {
                        enabled: true,
                        activeIndefinitely: false,
                        minutesAllowed: 30, // Default duration
                        createdAt: new Date().toISOString(),
                        startedAt: new Date().toISOString()
                    });
                } else {
                    store.setHotspotSharingForDevice(this.currentDeviceId, null);
                }
            }
        },
        hotspotSharingMode: {
            get() {
                return this.currentDeviceHotspotSharing?.activeIndefinitely ? 'indefinite' : 'fixed';
            },
            set(value) {
                if (!this.currentDeviceHotspotSharing) return;
                const store = useAppUsageRestrictionsStore();
                const update = { ...this.currentDeviceHotspotSharing };
                
                if (value === 'indefinite') {
                    update.activeIndefinitely = true;
                } else {
                    update.activeIndefinitely = false;
                    if (!update.minutesAllowed) {
                        update.minutesAllowed = 30; // Restore default if missing
                    }
                }
                store.setHotspotSharingForDevice(this.currentDeviceId, update);
            }
        },
        hotspotSharingDuration: {
            get() {
                return this.currentDeviceHotspotSharing?.minutesAllowed || 30;
            },
            set(value) {
                 if (!this.currentDeviceHotspotSharing) return;
                 const store = useAppUsageRestrictionsStore();
                 const update = { ...this.currentDeviceHotspotSharing };
                 update.minutesAllowed = value;
                 store.setHotspotSharingForDevice(this.currentDeviceId, update);
            }
        },
        hotspotDurationRules() {
            return [
                v => !!v || this.$gettext('Duration is required'),
                v => (v && v >= 1) || this.$gettext('Duration must be at least 1 minute'),
                 // Optional: Add max duration if needed, e.g., v => v <= 1440 || 'Max ...'
            ];
        }
    },
    methods: {
        formatDateTime(isoString) {
            if (!isoString) return '';
            try {
                return new Date(isoString).toLocaleString();
            } catch (e) {
                return isoString;
            }
        }
    },
    watch: {
        currentSelectedConfiguration: {
            immediate: true,
            handler(newVal) {
                if (newVal) {
                    const deviceConfig = this.configurations.find(
                        config => config.key === newVal
                    );
                    if (deviceConfig?.value?.userInfo?.mdmDeviceId) {
                        this.currentDeviceId = deviceConfig.value.userInfo.mdmDeviceId;
                    }
                }
            }
        }
    }
}
</script>